import { defineStore } from "pinia";
import { ExaminationProperty } from "./entity";
import { ExaminationPropertyList } from "./list.gen";
import type {
  ExaminationPropertyStore,
  ExaminationPropertyStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const examinationPropertyList = new ExaminationPropertyList();
export const useExaminationPropertyStore = defineStore(
  "examinationPropertyStore",
  {
    state: (): ExaminationPropertyStoreState => ({
      activeID: null,
      cache: {},
      list: [] as ExaminationProperty[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-ExaminationProperty", {
            detail: { id },
          }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.ExaminationProperty"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(examinationProperty: ExaminationProperty): Promise<void> {
        // Set active
        await examinationProperty
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = examinationProperty.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(examinationProperty: ExaminationProperty): void {
        const id = examinationProperty.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(examinationProperty);

          // Set active
          if (this.active === null) {
            this.activeID = examinationProperty.id;
          }
        } else {
          this.reinsert(examinationProperty);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as ExaminationProperty[]).map((x: any) => {
          const examinationProperty = new ExaminationProperty({});
          examinationProperty.set(x as JSONData);
          return examinationProperty;
        });
      },
      async delete(examinationProperty: ExaminationProperty): Promise<void> {
        await examinationProperty
          .delete()
          .then(() => {
            this.remove(examinationProperty);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const examinationProperty = (this.list as ExaminationProperty[]).find(
          (x: ExaminationProperty) => x.id === id,
        );
        if (examinationProperty !== undefined) {
          await this.delete(examinationProperty);
        } else {
          throw new Error("Could not find ExaminationProperty with id: " + id);
        }
      },
      contains(id: string): boolean {
        return (this.list as ExaminationProperty[]).some(
          (x: ExaminationProperty) => x.id === id,
        );
      },
      get(id: string): ExaminationProperty | null {
        return (
          (this.list as ExaminationProperty[]).find(
            (x: ExaminationProperty) => x.id === id,
          ) || null
        );
      },
      reinsert(examinationProperty: ExaminationProperty): void {
        const index = (this.list as ExaminationProperty[]).findIndex(
          (x: ExaminationProperty) => x.id === examinationProperty.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            examinationProperty,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(examinationProperty);
        }
        this.updateKey += 1;
      },
      remove({ id }: ExaminationProperty): void {
        const index = (this.list as ExaminationProperty[]).findIndex(
          (x: ExaminationProperty) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(id: string, force = false): Promise<ExaminationProperty> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const examinationProperty = this.get(id);
          if (examinationProperty !== null) {
            return examinationProperty;
          }
        }

        // Retrieve examinationProperty
        const examinationProperty = new ExaminationProperty({});

        // Get examinationProperty
        await examinationProperty.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate examinationProperty
          await this.activate(entity);
        });

        return examinationProperty;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: ExaminationProperty[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as ExaminationProperty[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await examinationPropertyList.getAll().then(async (list) => {
          list.forEach((examinationProperty) => {
            this.reinsert(examinationProperty);
          });

          // Activate examinationProperty
          if (list.length > 0) {
            if (examinationPropertyList.active !== null) {
              this.activeID = examinationPropertyList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty("examination_property_id", this.activeID);
        }

        return {
          active: this.activeID,
          items: this.list as ExaminationProperty[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: ExaminationProperty[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as ExaminationProperty,
            ),
          };
        }

        // Retrieve list
        const items = await examinationPropertyList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((examinationProperty) => {
              this.reinsert(examinationProperty);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: ExaminationProperty) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: ExaminationProperty[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as ExaminationProperty,
            ),
          };
        }

        // Retrieve list
        const items = await examinationPropertyList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((examinationProperty) => {
              this.reinsert(examinationProperty);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: ExaminationProperty) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: ExaminationProperty[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as ExaminationProperty,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await examinationPropertyList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((examinationProperty) => {
              this.reinsert(examinationProperty);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: ExaminationProperty) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(examinationProperty: ExaminationProperty): Promise<string> {
        // Update examinationProperty
        await examinationProperty.update();

        // Reinsert examinationProperty
        this.reinsert(examinationProperty);

        // Activate examinationProperty
        if (this.active === null) {
          await this.activate(examinationProperty);
        }

        this.emitStoreUpdateEvent();

        return examinationProperty.id;
      },
    },
    getters: {
      active(): ExaminationProperty | null {
        if (this.activeID === null) {
          return null;
        }

        const examinationProperty = (this.list as ExaminationProperty[]).find(
          (x: ExaminationProperty) => x.id === this.activeID,
        );
        if (examinationProperty === undefined) {
          return null;
        }

        return examinationProperty;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createExaminationPropertyStore(): ExaminationPropertyStore {
  const store = useExaminationPropertyStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as ExaminationPropertyStore; // TODO: Fix this
}
